import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { OverridableToolbarView } from '@bingads-webui/toolbar';
import {
  CampaignAdgroupSelectorFilterAgent,
  EmptyFilterView,
} from '@bingads-webui-campaign-react/campaign-adgroup-selector-filter';

import {
  withGetSummary,
  getDetailsViewModel,
} from '@bingads-webui-campaign/recommendation-core';

import { DetailsSummary } from './details-summary';
import { DetailsRecommendations } from './details-recommendations';

import { DetailsToolbar } from './details-toolbar';

class detailsView extends React.PureComponent {
  static propTypes = {
    context: PropTypes.shape({
      Level: PropTypes.string.isRequired,
    }).isRequired,
    data: PropTypes.objectOf(PropTypes.any),
    campaignAdGroups: PropTypes.arrayOf(PropTypes.object),
    deps: PropTypes.shape({
      sessionStore: PropTypes.object.isRequired,
      i18n: PropTypes.object.isRequired,
      filterPreferences: PropTypes.shape({
        getFilters: PropTypes.func,
      }).isRequired,
      cacheService: PropTypes.object.isRequired,
      jsdata: PropTypes.object.isRequired,
      edmCampaign: PropTypes.object.isRequired,
      dataContextManager: PropTypes.object.isRequired,
      locationMap: PropTypes.func.isRequired,
      preferencesService: PropTypes.objectOf(PropTypes.any).isRequired,
      appConfig: PropTypes.objectOf(PropTypes.any).isRequired,
      constants: PropTypes.shape({
        imagePath: PropTypes.string.isRequired,
      }).isRequired,
      permissions: PropTypes.object.isRequired,
    }).isRequired,
    currency: PropTypes.string.isRequired,
    scope: PropTypes.shape({
      levelAt: PropTypes.string.isRequired,
    }).isRequired,
    type: PropTypes.string.isRequired,
    reload: PropTypes.func.isRequired,
    dataService: PropTypes.objectOf(PropTypes.any).isRequired,
    viewSummary: PropTypes.func.isRequired,
    el: PropTypes.string.isRequired,
    filterConfig: PropTypes.objectOf(PropTypes.any).isRequired,
    filterService: PropTypes.objectOf(PropTypes.any).isRequired,
    preferencesName: PropTypes.string.isRequired,
    pageSize: PropTypes.number,
    pageNumber: PropTypes.number,
    currentActivity: PropTypes.objectOf(PropTypes.any).isRequired,
    hasFilter: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool,
    guid: PropTypes.string.isRequired, // for adinsight view event log in aria
    initialToolbarConfig: PropTypes.objectOf(PropTypes.any),
    // eslint-disable-next-line react/forbid-prop-types
    toolbar: PropTypes.any,
  };

  static defaultProps = {
    data: null,
    campaignAdGroups: null,
    pageSize: null,
    pageNumber: null,
    readOnly: false,
    initialToolbarConfig: null,
    toolbar: null,
  };

  constructor(props) {
    super(props);
    this.upperToolBarInstance = new OverridableToolbarView({
      items: [{
        type: 'dropdown',
        id: 'filter-menu',
      }],
    });
    this.recommendationsRef = React.createRef();
    this.state = {
      toolbarConfig: props.initialToolbarConfig,
      hideToolbar: false,
      appliedFilter: undefined,
    };
  }

  componentDidMount() {
    this.upperToolBarInstance.render();
  }

  componentDidUpdate(prevProps) {
    if (this.props.type !== prevProps.type) {
      this.logMessage(`reload page: props.type changed from ${prevProps.type} to ${this.props.type}`);
      this.props.reload();
    }
  }

  componentWillUnmount() {
    this.upperToolBarInstance.remove();
  }

  onFilterStateChanged = () => {
    this.logMessage('reload page: filter state changed');
    this.props.reload();
  }

  getUserInputs = () => {
    const instance = this.recommendationsRef.current;
    return instance.getUserInputs();
  }

  updateToolbar = (values, forceUpdate) => {
    _.each(_.isArray(values) ? values : [values], (value) => {
      const { id } = value;

      if (this.state.toolbarConfig.has(id)) {
        this.state.toolbarConfig.set(id, _.extend(this.state.toolbarConfig.get(id), value));
        this.setState({
          toolbarConfig: this.state.toolbarConfig,
        });
        if (id === 'predicate-list' && !_.isEqual(this.state.appliedFilter, this.props.filterService.editableFilter)) {
          this.setState({
            appliedFilter: this.props.filterService.editableFilter,
          });
          this.forceUpdate();
        } else if (forceUpdate) {
          this.forceUpdate();
        }
      }
    });
  }

  resetToolbarVisibility = ({ forceHideToolbar }) => {
    this.setState({
      hideToolbar: forceHideToolbar,
    });
  }

  logMessage(msg) {
    this.props.currentActivity.trace(msg, 'recommendation/details');
  }

  render() {
    this.logMessage(`view detailed competition recommendations for opportunity type ${this.props.type}`);

    const hasData = this.props.data && this.props.data[this.props.type];
    let summaryView = null;
    let detailsRecommendationsView = null;
    let emptyView = null;
    let opForToolBar = _.extend({
      i18n: this.props.deps.i18n,
      getUserInputs: this.getUserInputs,
      callback: this.props.viewSummary,
      total: 0,
      initialToolbarConfig: this.props.initialToolbarConfig,
    }, _.pick(this.props, 'context', 'dataService', 'downloadService', 'el', 'filterService', 'type', 'viewSummary', 'campaignAdGroups', 'readOnly', 'guid'));

    if (hasData) {
      const normalizedData = this.props.data[this.props.type];
      const {
        deps: {
          locationMap, i18n, preferencesService, appConfig,
        },
      } = this.props;
      const { summary, recommendations } = getDetailsViewModel(_.extend(
        {},
        normalizedData, {
          i18n,
          currency: this.props.currency,
          scope: this.props.scope.levelAt,
          appConfig,
          permissions: this.props.deps.permissions,
        }
      ));
      const op = _.defaults({
        data: recommendations,
        deps: { locationMap, i18n, preferencesService },
        total: normalizedData.recommendationsCount,
      }, _.pick(
        this.props,
        'context',
        'dataService',
        'el',
        'preferencesName',
        'reload',
        'type',
        'pageSize',
        'pageNumber',
        'currency',
        'currentActivity',
        'readOnly',
        'guid',
        'logExpInfo'
      ));

      summaryView = <DetailsSummary {...summary} i18n={i18n} />;
      detailsRecommendationsView = (<DetailsRecommendations
        {...op}
        ref={this.recommendationsRef}
      />);

      opForToolBar = _.extend(opForToolBar, {
        scope: this.props.scope.levelAt,
        aggregateLevel: normalizedData.level,
        total: op.total,
        logExpInfo: op.logExpInfo,
        updateToolbar: this.updateToolbar,
      }, summary);
    } else if (this.props.hasFilter) {
      this.logMessage('there is no filter result, will show empty filter view');
      emptyView = (<EmptyFilterView
        i18n={this.props.deps.i18n}
        imagePath={this.props.deps.constants.imagePath}
      />);
    } else {
      this.logMessage('there is no data, will direct to summary view');
      this.props.viewSummary();
    }

    if (this.props.filterService) {
      this.props.filterService.on('filter-state-changed', this.onFilterStateChanged);
    }
    let toolbarVNext = null;
    if (this.props.initialToolbarConfig) {
      const ToolbarFilterbarSet = this.props.toolbar;
      toolbarVNext = (<ToolbarFilterbarSet
        hideToolbar={this.state.hideToolbar}
        toolbarConfig={this.state.toolbarConfig}
        updateToolbar={this.updateToolbar}
        resetToolbarVisibility={this.resetToolbarVisibility}
      />);
    }

    return (
      <div className="recommendation-details-view">
        {summaryView}
        <DetailsToolbar {...opForToolBar} >
          {this.props.initialToolbarConfig ? toolbarVNext : (
            <CampaignAdgroupSelectorFilterAgent
              filterConfig={this.props.filterConfig}
              filterService={this.props.filterService}
              upperToolBar={this.upperToolBarInstance}
              onFilterStateChanged={this.onFilterStateChanged}
            />
          )}
        </DetailsToolbar>
        {detailsRecommendationsView}
        {emptyView}
      </div>
    );
  }
}

export const DetailsView = withGetSummary(/* withDetails */ true)(detailsView);
